import { defineComponent as _defineComponent } from 'vue'
import BCXSuggestionBox from '@/components/molecules/BCXSuggestionBox.vue';
import { computed, PropType, ref } from 'vue';
import BCXUserListItem from '@/components/molecules/BCXUserListItem.vue';
import { Contract, ContractStatus } from '@/models/Contracts';
import useUser from '@/mixins/useUser';


export default /*#__PURE__*/_defineComponent({
  __name: 'InvoicesActionContractSelector',
  props: {
  contracts: {
    type: Array as PropType<Contract[]>,
    default: () => []
  }
},
  emits: ['select'],
  setup(__props, { emit }) {

const props = __props;



const { tUserFallback: t } = useUser('self');


const searchTerm = ref('');

const onSelect = (item:any) => {
  emit('select', item);
};

/**
 * Returns a sorted list of contracts, prioritizing those with status 'SIGNED' first,
 * then 'OPEN', and all others last. The original array is not mutated.
 */
const sortContractsByStatus = computed(() => props.contracts.slice().sort((a, b) => {
  const order = { SIGNED: 0, OPEN: 1 };
  return (order[a.status as keyof typeof order] ?? 2) - (order[b.status as keyof typeof order] ?? 2);
}));

/**
 * Ensures that each contract in the array has a `client.user` object.
 * If `client.user` is missing, it is created using the client company data,
 * with the role 'ROOT' and the company's root user ID. Required for display in BCXUserListItem
 *
 * @param contracts - Array of Contract objects to process
 * @returns {Contract[]} A new array of contracts with guaranteed `client.user` property
 */
function ensureThatUserExistsInClient(contracts: Contract[]): Contract[] {
  return contracts.slice().map((contract) => {
    const clientCompany = contract.client.company;
    contract.client.user ??= {
      company: clientCompany,
      companyRole: 'ROOT',
      userId: clientCompany?.rootUserId
    };
    return contract;
  });
}

/**
 * Filters an array of Contract objects by a search term.
 * The function checks if the search term is included in any of the following fields:
 * - client.user.firstname
 * - client.user.lastname
 * - client.user.company.name
 * - contract.reference
 * The search is case-insensitive. If the search term is empty, the original array is returned.
 *
 * @param contracts - Array of Contract objects to filter
 * @returns {Contract[]} Filtered array of contracts matching the search term
 */
function filterContractsBySearchTerm(contracts: Contract[]): Contract[] {
  const term = searchTerm.value.trim().toLowerCase();
  if (!term) return contracts;

  return contracts.filter((contract) => {
    const { user } = contract.client;
    const company = user?.company;
    return [
      user?.firstname,
      user?.lastname,
      company?.name,
      contract.reference
    ].some((field) => field?.toLowerCase().includes(term));
  });
}

/**
 * Returns a filtered and normalized list of contracts for display.
 * - Ensures each contract has a `client.user` property (creates one if missing).
 * - Sorts contracts by status: 'SIGNED' first, then 'OPEN', then others.
 * - Filters contracts by the current search term (case-insensitive) in
 *   `client.user.firstname`, `client.user.lastname`, `client.user.company.name`, or `contract.reference`.
 *
 * @returns {Contract[]} The filtered and normalized contracts array.
 */
const revisedContacts = computed(() => {
  const contractsWithUser = ensureThatUserExistsInClient(sortContractsByStatus.value);
  return filterContractsBySearchTerm(contractsWithUser);
});

/**
 * Finds the index of the first contract with the given status in the sorted contracts list.
 * @param status - The contract status to search for (e.g., 'SIGNED' or 'OPEN').
 * @returns The index of the first contract with the specified status, or -1 if not found.
 */
function findFirstSignedContractIndex(status: ContractStatus): number {
  return revisedContacts.value.findIndex((contract) => contract.status === status);
}


return { __sfc: true,props, t, emit, searchTerm, onSelect, sortContractsByStatus, ensureThatUserExistsInClient, filterContractsBySearchTerm, revisedContacts, findFirstSignedContractIndex, BCXSuggestionBox, BCXUserListItem }
}

})